import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { CheckIcon, PauseIcon, PlayIcon, StopIcon } from "@heroicons/react/24/solid";
import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { Renewal, DueDateStatus } from "../interfaces/renewal_types"

export function InstructionDueDate(
    {renewal, status}: {renewal: Renewal, status: DueDateStatus}
    //{instructionDueDate, status, maintenanceAction}:
    //{instructionDueDate: string, status: DueDateStatus, maintenanceAction?: MaintenanceAction}
) {
    const {t} = useTranslation()

    if (renewal.feeDueDate === undefined) return null

    const isDueDateClose = status === 'critical' && !renewal.isAutoPay
    const isPastDueDate = (status === 'manual-instruction' || status === 'too-late') &&
        !renewal.isAutoPay

    //console.log({status, isDueDateClose, isPastDueDate, isNeededToContactDm})

    const statusBubble = <div className={clsx(
        isPastDueDate
        ? "bg-warn-400/20 text-warn-400" 
        : isDueDateClose ? "bg-yellow-400/20 text-yellow-400"
        : "bg-pcx-400/20 text-pcx-400",
        "p-0 rounded-full block")}
    >
        <div className="w-2 h-2 bg-current rounded-full" />
    </div>

    return (
        <>
            <div className="my-auto">{statusBubble}</div>
            <div
                title={t('due')}
                className={clsx(
                    "tabular-nums font-semibold md:text-lg inline-flex items-center gap-2",
                    isPastDueDate ? "text-warn-700" :
                        isDueDateClose ? "text-yellow-700" :
                            "text-pcx-900",
                )}>
                {renewal.instructionDueDate}
            </div>
            <div className={
                clsx(
                    isPastDueDate ? "text-warn-900" : "text-slate-600",
                    "text-sm align-middle whitespace-nowrap")}>
                {isPastDueDate
                    ? t('past-instruction-due-date')
                    : t('instruction-due')}
            </div>
        </>
    )
}

export function FeeAmount({amount, currency}: {amount: number | string, currency: string}) {
    return (
        <div className='md:text-lg font-semibold text-pcx-900 tabular-nums whitespace-nowrap self-end'>
            {amount} <span className="text-slate-500 font-normal text-sm">{currency}</span>
        </div>
    )
}

const instructionLabels = {
    'Pay': 'ip-right-pay',
    'Cancel': 'ip-right-cancel',
    'Hold': 'ip-right-hold',
    'Skip': 'ip-right-skip',
}

function getPhase(renewal: Renewal, t: (key: string, opts?: any) => string) {
  let phase = renewal.state as string

  if (renewal.isAutoPay) {
    if (renewal.state === 'Open') {
      phase = 'fee-will-be-paid-automatically'
    } else if (renewal.state === 'Paid') {
      phase = 'fee-has-been-paid-automatically'
    } else if (renewal.state === 'Cancelled') {
      phase = 'fee-has-not-been-paid'
    }
  } else {
    if (renewal.state === 'Open') {
      if (renewal.instruction.instruction === 'Pay') return 'instructed-this-fee-will-be-paid'
      else if (renewal.instruction.instruction === 'Cancel') return 'fee-will-not-be-paid'
      else return 'instruct-or-the-fee-will-not-be-paid'
    } else if (renewal.state === 'Paid') {
      phase = 'fee-has-been-paid'
    } else if (renewal.state === 'Cancelled') {
      phase = 'fee-has-not-been-paid'
    }
  }

  if (renewal.instruction !== undefined) {
    phase =
      t('instructed-on', { date: renewal.instruction.created.slice(0, 10) }) + ': ' + t(instructionLabels[renewal.instruction.instruction])
  } else {
    phase = t(phase)
  }

  return phase
}


export function getIcon(renewal: Renewal) {
  if (renewal.instruction !== undefined) {
    switch (renewal.instruction.instruction) {
      case 'Pay':
        return <CheckIcon className="w-5 h-5 text-pcx-500" />
      case 'Cancel':
        return <StopIcon className="w-5 h-5 text-warn-300" />
      default:
        return <QuestionMarkCircleIcon className="w-5 h-5 text-slate-500" />
    }
  }
  if (renewal.isAutoPay) {
    switch (renewal.state) {
      case 'Open':
        return <PlayIcon className="w-5 h-5 text-pcx-500" />
      case 'Paid':
      case 'Instructed':
        return <CheckIcon className="w-5 h-5 text-pcx-500" />
      case 'Cancelled':
        return <StopIcon className="w-5 h-5 text-warn-300" />
      default:
        return <QuestionMarkCircleIcon className="w-5 h-5 text-slate-500" />
    }
  } else {
    switch (renewal.state) {
      case 'Open':
        return <PauseIcon className="w-5 h-5 text-pcx-300" />
      case 'Paid':
      case 'Instructed':
        return <CheckIcon className="w-5 h-5 text-pcx-500" />
      case 'Cancelled':
        return <StopIcon className="w-5 h-5 text-warn-300" />
      default:
        return <QuestionMarkCircleIcon className="w-5 h-5 text-slate-500" />
    }
  }
}

//const unknownIcon = <QuestionMarkCircleIcon className="w-5 h-5 text-slate-500" />

export function StatusView(
    {renewal, status, deprecated = false}: {renewal: Renewal, status: DueDateStatus, deprecated?: boolean}
    //{maintenanceAction, status, instruction, deprecated = false}: 
    //{maintenanceAction: MaintenanceAction, status: DueDateStatus, instruction?: PcInstruction, deprecated?: boolean}
) {
    const {t} = useTranslation()

    const phase = getPhase(renewal, t)
    const icon = getIcon(renewal)

    return (
        <div
            className={clsx("relative pl-6 text-left text-sm", deprecated ? "text-slate-400 line-through" : "text-pcx-800")}
            title={'Payment Provider: ' + renewal?.state + (deprecated ? ` (${t('superseded')})` : '')}
        >
            <div className={clsx("absolute left-0", deprecated && "opacity-20")}>
                {icon ?? <div className="w-5 h-5" />}
            </div>
            {phase}
        </div>
    )
}